import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ArrowRight, Flame, Percent, Sparkles, Award, Clock, Star, Package,
} from "lucide-react";
import api from "@/lib/api";
import { Loader } from "@/components/common";
import ProductCard from "@/components/ProductCard";
import useProductRatings from "@/hooks/useProductRatings";
import { hasPromo, discountPercent, effectivePrice, formatFCFA } from "@/lib/format";
import PromoCountdown from "@/components/PromoCountdown";

const UNIVERSE_IMAGES = [
  "https://images.unsplash.com/photo-1637241612956-b7309005288b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1540103711724-ebf833bde8d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1606676539940-12768ce0e762?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1673978484487-6c448dd1eb4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1603080296081-81f47189df91?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1588399944136-efbd34b99d6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];
const HERO_FALLBACK = "https://images.unsplash.com/photo-1540103711724-ebf833bde8d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BANNER_IMAGE = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ROW_COLORS = ["#C85A32", "#0F172A", "#E08B00", "#166534"];

export default function Home() {
  const ratings = useProductRatings();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [flash, setFlash] = useState(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, p, s, f, bs] = await Promise.all([
          api.get("/categories"), api.get("/products"), api.get("/site-settings"),
          api.get("/flash-sale/active").catch(() => ({ data: null })),
          api.get("/products/best-sellers?limit=12").catch(() => ({ data: [] })),
        ]);
        setCategories(c.data); setProducts(p.data); setSettings(s.data); setFlash(f.data); setBestSellers(bs.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const promos = [...products.filter(hasPromo)].sort((a, b) => discountPercent(b) - discountPercent(a));
  const nowMs = Date.now();
  const bannerInWindow = (b) => {
    const s = b.start ? new Date(b.start).getTime() : null;
    const e = b.end ? new Date(b.end).getTime() : null;
    if (s && !Number.isNaN(s) && nowMs < s) return false;
    if (e && !Number.isNaN(e) && nowMs > e) return false;
    return true;
  };
  const banners = (settings?.banners || []).filter((b) => b.enabled !== false && b.image && bannerInWindow(b));
  const slides = banners.length
    ? banners.map((b) => ({ kind: "banner", ...b }))
    : promos.slice(0, 5).map((p) => ({ kind: "promo", p }));
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setSlide((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  if (loading) return <Loader />;

  const home = settings?.home || {};
  const comm = home.communication, infl = home.influencer, proj = home.project;
  const featured = products.filter((p) => p.featured);
  const newest = [...products].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 12);
  const flashProducts = flash?.products || [];
  const rowCategories = categories
    .map((c) => ({ cat: c, items: products.filter((p) => p.category_id === c.id) }))
    .filter((r) => r.items.length > 0)
    .slice(0, 4);
  const recent = (() => {
    let ids = [];
    try { ids = JSON.parse(localStorage.getItem("togobati_recent") || "[]"); } catch { ids = []; }
    const byId = Object.fromEntries(products.map((p) => [p.id, p]));
    const seen = ids.map((id) => byId[id]).filter(Boolean);
    return seen.length >= 3 ? seen.slice(0, 10) : products.slice(0, 8);
  })();

  return (
    <div className="space-y-8 sm:space-y-10">
      {/* ============ HERO CARROUSEL (bannières gérables sinon promos) ============ */}
      {slides.length > 0 ? (
        <section className="relative rounded-2xl overflow-hidden shadow-lg group min-h-[240px] sm:min-h-[320px] lg:min-h-[380px]" data-testid="hero-carousel">
          {slides.map((sl, i) => {
            const to = sl.kind === "banner" ? (sl.link || "/recherche") : `/produit/${sl.p.id}`;
            const img = sl.kind === "banner" ? sl.image : (sl.p.images || [])[0] || HERO_FALLBACK;
            const title = sl.kind === "banner" ? sl.title : sl.p.name;
            return (
              <Link key={i} to={to} data-testid={`hero-slide-${i}`}
                className={`absolute inset-0 transition-opacity duration-700 ${i === slide % slides.length ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#7c2d12] to-[#C85A32]" />
                {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
                <div className="relative h-full px-5 sm:px-10 py-8 sm:py-12 flex flex-col justify-center max-w-xl">
                  {sl.kind === "promo" && (
                    <span className="inline-flex w-fit items-center gap-1.5 bg-[#DC2626] text-white text-[11px] sm:text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
                      <Flame className="w-3.5 h-3.5" /> Offre exclusive · −{discountPercent(sl.p)}%
                    </span>
                  )}
                  <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.05] line-clamp-2">{title}</h2>
                  {sl.kind === "banner" && sl.subtitle && <p className="mt-2 text-slate-200 text-sm sm:text-lg max-w-md line-clamp-2">{sl.subtitle}</p>}
                  {sl.kind === "promo" && (
                    <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                      <span className="font-num font-black text-[#FDBA74] text-2xl sm:text-3xl">{formatFCFA(effectivePrice(sl.p))}</span>
                      <span className="font-num text-slate-300 line-through text-sm sm:text-base">{formatFCFA(sl.p.price)}</span>
                    </div>
                  )}
                  <span className="mt-5 inline-flex w-fit items-center gap-2 bg-white text-[#0F172A] hover:bg-[#FDF3EE] font-bold px-5 sm:px-6 py-2.5 sm:py-3 rounded-full transition">
                    J'en profite <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            );
          })}
          {slides.length > 1 && (
            <>
              <button onClick={() => setSlide((s) => (s - 1 + slides.length) % slides.length)} aria-label="Précédent" data-testid="hero-prev"
                className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white items-center justify-center shadow transition">
                <ChevronLeft className="w-5 h-5 text-[#0F172A]" />
              </button>
              <button onClick={() => setSlide((s) => (s + 1) % slides.length)} aria-label="Suivant" data-testid="hero-next"
                className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white items-center justify-center shadow transition">
                <ChevronRight className="w-5 h-5 text-[#0F172A]" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`}
                    className={`h-2 rounded-full transition-all ${i === slide % slides.length ? "bg-[#E08B00] w-6" : "bg-white/60 w-2"}`} />
                ))}
              </div>
            </>
          )}
        </section>
      ) : (
        <section className="relative rounded-2xl overflow-hidden bg-[#0F172A] text-white" data-testid="hero-carousel">
          <img src={HERO_FALLBACK} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="relative px-6 py-12 sm:py-16 max-w-2xl">
            <span className="inline-block bg-[#E08B00] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">Lomé · Agoè · Baguida</span>
            <h1 className="font-display text-3xl sm:text-5xl font-black uppercase leading-none tracking-tight">Vos matériaux livrés<br /><span className="text-[#E08B00]">et installés</span></h1>
            <p className="mt-4 text-slate-200 text-sm sm:text-base max-w-lg">Commandez vos matériaux, demandez un devis et une installation — suivez tout en un seul endroit.</p>
            <Link to="/recherche" className="inline-block mt-6 bg-[#C85A32] hover:bg-[#A94520] transition text-white font-bold px-6 py-3 rounded-lg" data-testid="hero-cta">Explorer le catalogue</Link>
          </div>
        </section>
      )}

      {/* ============ À LA UNE (produits vedettes épinglés) ============ */}
      {featured.length > 0 && (
        <section data-testid="featured-section">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-[#E08B00] text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"><Star className="w-3.5 h-3.5 fill-white" /> À la une</span>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0F172A]">Nos coups de cœur</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.id} p={p} rating={ratings[p.id]} />)}
          </div>
        </section>
      )}

      {/* ============ VENTES FLASH (programmées sinon promos) ============ */}
      {(flashProducts.length > 0 || promos.length > 0) && (
        <section className="rounded-2xl bg-gradient-to-r from-red-900 via-rose-900 to-[#0F172A] text-white p-4 sm:p-6 shadow-xl" data-testid="ventes-flash-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-white/10">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-black tracking-tight">
                {flash?.title || "Ventes Flash"} <span className="text-white/60 font-bold text-base sm:text-lg">| {flash ? "Offre limitée" : "Chaque jour"}</span>
              </h2>
              <span className="inline-flex items-center gap-1.5 bg-black/30 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold" data-testid="flash-countdown">
                <Clock className="w-4 h-4" /> <PromoCountdown end={flash?.end || nextMidnightISO()} compact />
              </span>
            </div>
            <Link to="/recherche" className="inline-flex items-center gap-1.5 bg-white text-red-900 hover:bg-red-50 font-bold text-sm px-4 py-2 rounded-full transition" data-testid="flash-see-all">
              Voir plus <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <HScroll testid="flash-scroll">
            {(flashProducts.length > 0 ? flashProducts : promos.slice(0, 12)).map((p) => <FlashCard key={p.id} p={p} rating={ratings[p.id]} />)}
          </HScroll>
        </section>
      )}

      {/* ============ PRODUITS ESSENTIELS (raccourcis) ============ */}
      <section data-testid="essentiels-shortcuts">
        <h2 className="font-display text-xl sm:text-2xl font-bold text-[#C85A32] leading-tight">Trouvez tous vos essentiels</h2>
        <p className="text-sm text-slate-500 mb-4">Des matériaux au meilleur prix, livrés et posés</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { name: "Meilleures ventes", sub: "Les plus commandés", anchor: "meilleures-ventes", color: "from-amber-500 to-orange-600", Icon: Flame },
            { name: "Déstockage", sub: "Promos du moment", to: "/recherche?promo=1", color: "from-emerald-600 to-teal-700", Icon: Percent },
            { name: "Nouveaux matériaux", sub: "Derniers arrivages", anchor: "nouveautes", color: "from-blue-600 to-indigo-700", Icon: Sparkles },
            { name: "Devenir influenceur", sub: "Gagnez des commissions", to: "/devenir-influenceur", color: "from-purple-600 to-fuchsia-700", Icon: Award },
          ].map((s) => {
            const cls = `relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.color} text-white p-4 min-h-[92px] flex flex-col justify-between hover:brightness-110 hover:-translate-y-0.5 transition-all shadow-sm`;
            const inner = (<>
              <s.Icon className="w-7 h-7 opacity-90" />
              <div>
                <p className="font-display font-extrabold leading-tight">{s.name}</p>
                <p className="text-[11px] text-white/80">{s.sub}</p>
              </div>
              <ArrowRight className="w-4 h-4 absolute top-4 right-4 opacity-70" />
            </>);
            const tid = `shortcut-${s.name.toLowerCase().replace(/\s/g, "-")}`;
            if (s.anchor) {
              return (
                <button key={s.name} type="button" data-testid={tid} className={`${cls} text-left`}
                  onClick={() => document.getElementById(s.anchor)?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                  {inner}
                </button>
              );
            }
            return <Link key={s.name} to={s.to} data-testid={tid} className={cls}>{inner}</Link>;
          })}
        </div>
      </section>

      {/* ============ MEILLEURES VENTES (les plus commandés) ============ */}
      {bestSellers.length > 0 && (
        <section id="meilleures-ventes" data-testid="best-sellers-section" className="scroll-mt-20">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"><Flame className="w-3.5 h-3.5" /> Top ventes</span>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0F172A]">Meilleures ventes</h2>
          </div>
          <HScroll testid="best-sellers-scroll">
            {bestSellers.map((p) => <ProductCard key={p.id} p={p} rating={ratings[p.id]} />)}
          </HScroll>
        </section>
      )}

      {/* ============ NOUVEAUTÉS (derniers ajoutés) ============ */}
      {newest.length > 0 && (
        <section id="nouveautes" data-testid="newest-section" className="scroll-mt-20">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"><Sparkles className="w-3.5 h-3.5" /> Nouveautés</span>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0F172A]">Nouveaux matériaux</h2>
          </div>
          <HScroll testid="newest-scroll">
            {newest.map((p) => <ProductCard key={p.id} p={p} rating={ratings[p.id]} />)}
          </HScroll>
        </section>
      )}

      {/* ============ NOS UNIVERS (grille photo) ============ */}
      {categories.length > 0 && (
        <section data-testid="nos-univers-grid">
          <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-[#0F172A] leading-tight">Vos matériaux en un seul endroit</h2>
          <p className="text-sm text-slate-500 mb-4">Nos univers</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {categories.slice(0, 12).map((c, i) => (
              <Link key={c.id} to={`/recherche?category=${c.id}`} data-testid={`univers-${c.slug || c.id}`}
                className="relative rounded-xl overflow-hidden shadow-sm group hover:shadow-lg transition-all aspect-[4/5] sm:aspect-square">
                <img src={c.icon_image || UNIVERSE_IMAGES[i % UNIVERSE_IMAGES.length]} alt={c.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 p-3 flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-1">
                    <h3 className="font-display font-bold text-white text-sm sm:text-base leading-tight line-clamp-2">{c.name}</h3>
                    <ArrowRight className="w-4 h-4 text-white shrink-0" />
                  </div>
                  <span className="inline-flex w-fit items-center bg-white text-[#0F172A] text-[11px] font-bold px-3 py-1 rounded-full">Découvrir</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ============ RANGÉES PAR CATÉGORIE ============ */}
      {rowCategories.map((r, idx) => {
        const color = ROW_COLORS[idx % ROW_COLORS.length];
        return (
          <section key={r.cat.id} data-testid={`category-row-${r.cat.id}`}>
            <div className="flex items-center justify-between gap-3 rounded-t-2xl px-4 sm:px-6 py-3 sm:py-4" style={{ background: color }}>
              <h2 className="font-display text-lg sm:text-2xl font-black text-white leading-tight truncate">{r.cat.name}</h2>
              <Link to={`/recherche?category=${r.cat.id}`} data-testid={`row-see-all-${r.cat.id}`}
                className="inline-flex items-center gap-1.5 bg-white/95 hover:bg-white font-bold text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition shrink-0" style={{ color }}>
                Voir plus <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-white border border-t-0 border-slate-200 rounded-b-2xl p-3 sm:p-4">
              <HScroll testid={`row-scroll-${r.cat.id}`}>
                {r.items.slice(0, 12).map((p) => (
                  <div key={p.id} className="flex-shrink-0 w-[150px] sm:w-[190px] md:w-[210px] snap-start">
                    <ProductCard p={p} rating={ratings[p.id]} />
                  </div>
                ))}
              </HScroll>
            </div>
          </section>
        );
      })}

      {/* ============ VOUS AVEZ VU CES PRODUITS ============ */}
      {recent.length > 0 && (
        <section className="rounded-2xl bg-[#0F172A] text-white p-4 sm:p-6 shadow-xl border border-slate-800" data-testid="recently-viewed">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="font-display text-lg sm:text-2xl font-black tracking-tight">Vous avez vu ces produits</h2>
            <Link to="/recherche" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 font-bold text-xs sm:text-sm px-3 py-1.5 rounded-full transition" data-testid="recent-see-all">
              Voir plus <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <HScroll testid="recent-scroll" dark>
            {recent.map((p) => (
              <Link key={p.id} to={`/produit/${p.id}`} data-testid={`recent-card-${p.id}`}
                className="flex-shrink-0 w-[220px] snap-start bg-slate-800 rounded-xl p-3 border border-slate-700 hover:border-[#C85A32] transition-colors flex items-center gap-3">
                <div className="w-14 h-14 rounded-lg bg-slate-700 overflow-hidden shrink-0">
                  {(p.images || [])[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-500"><Package className="w-6 h-6" /></div>}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white line-clamp-2 leading-snug">{p.name}</p>
                  <p className="font-num font-bold text-[#FDBA74] text-sm mt-1">{formatFCFA(effectivePrice(p))}</p>
                </div>
              </Link>
            ))}
          </HScroll>
        </section>
      )}

      {/* ============ BANNIÈRE PROMO ============ */}
      <section className="relative rounded-2xl overflow-hidden shadow-lg" data-testid="promo-banner-card">
        <img src={BANNER_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/70 to-transparent" />
        <div className="relative p-6 sm:p-10 max-w-xl text-white">
          <h2 className="font-display text-2xl sm:text-3xl font-black leading-tight">Un chantier ? On s'occupe de tout.</h2>
          <p className="mt-2 text-slate-200 text-sm sm:text-base">Matériaux de qualité, livraison rapide à Lomé, Agoè & Baguida, et installation par nos pros certifiés.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/projet" className="inline-flex items-center gap-2 bg-[#E08B00] hover:bg-[#C77A00] transition text-white font-bold px-5 py-3 rounded-full" data-testid="banner-project-cta">Décrire mon projet <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/recherche" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition text-white font-bold px-5 py-3 rounded-full" data-testid="banner-shop-cta">Voir le catalogue</Link>
          </div>
        </div>
      </section>

      {/* ============ CMS : communication ============ */}
      {comm?.enabled && (
        <section className="rounded-2xl bg-gradient-to-br from-[#FDF3EE] to-white border border-[#C85A32]/10 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6" data-testid="home-communication">
          {comm.image && <img src={comm.image} alt="" className="w-full sm:w-56 h-40 object-cover rounded-xl shrink-0" />}
          <div>
            <h2 className="font-display text-2xl font-bold text-[#0F172A]">{comm.title}</h2>
            <p className="text-slate-600 mt-2 whitespace-pre-line">{comm.text}</p>
          </div>
        </section>
      )}

      {/* ============ CMS : influenceurs ============ */}
      {infl?.enabled && (
        <section className="rounded-2xl bg-[#0F172A] text-white p-6 sm:p-10 text-center" data-testid="home-influencer">
          <span className="inline-block bg-[#E08B00] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">Programme d'affiliation</span>
          <h2 className="font-display text-2xl sm:text-3xl font-black">{infl.title}</h2>
          <p className="text-slate-300 mt-2 max-w-xl mx-auto whitespace-pre-line">{infl.text}</p>
          <Link to={infl.button_link || "/devenir-influenceur"} className="inline-block mt-5 bg-[#C85A32] hover:bg-[#A94520] transition text-white font-bold px-6 py-3 rounded-lg" data-testid="home-influencer-cta">{infl.button_label || "Devenir influenceur"}</Link>
        </section>
      )}

      {/* ============ CMS : votre projet ============ */}
      {proj?.enabled && (
        <section className="relative rounded-2xl overflow-hidden bg-[#1E293B] text-white" data-testid="home-project">
          {proj.image && <img src={proj.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
          <div className="relative px-6 py-10 sm:py-12 max-w-2xl">
            <h2 className="font-display text-2xl sm:text-3xl font-black">{proj.title}</h2>
            <p className="text-slate-200 mt-2 whitespace-pre-line">{proj.text}</p>
            <Link to={proj.button_link || "/projet"} className="inline-block mt-5 bg-[#E08B00] hover:bg-[#C77A00] transition text-white font-bold px-6 py-3 rounded-lg" data-testid="home-project-cta">{proj.button_label || "Décrire mon projet"}</Link>
          </div>
        </section>
      )}
    </div>
  );
}

function nextMidnightISO() {
  const d = new Date();
  d.setHours(24, 0, 0, 0);
  return d.toISOString();
}

function HScroll({ children, testid, dark }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * (ref.current.clientWidth * 0.8), behavior: "smooth" });
  return (
    <div className="relative group/scroll">
      <div ref={ref} className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth pb-2 snap-x no-scrollbar" data-testid={testid}>
        {children}
      </div>
      <button onClick={() => scroll(-1)} aria-label="Défiler à gauche"
        className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-9 h-9 rounded-full items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition bg-white text-[#0F172A] ${dark ? "" : "border border-slate-200"}`}>
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button onClick={() => scroll(1)} aria-label="Défiler à droite"
        className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-9 h-9 rounded-full items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition bg-white text-[#0F172A] ${dark ? "" : "border border-slate-200"}`}>
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}

function FlashCard({ p, rating }) {
  const img = (p.images || [])[0];
  const isFlash = p.flash_price != null;
  const eff = isFlash ? p.flash_price : effectivePrice(p);
  const oldP = p.price;
  const disc = oldP > 0 ? Math.round((1 - eff / oldP) * 100) : 0;
  const limit = isFlash ? Number(p.flash_quantity) || 0 : 0;
  const stock = Number(p.stock) || 0;
  const shown = limit > 0 ? Math.min(limit, stock || limit) : stock;
  const pct = Math.max(8, Math.min(100, Math.round((shown / 50) * 100)));
  return (
    <Link to={`/produit/${p.id}`} data-testid={`flash-card-${p.id}`}
      className="flex-shrink-0 w-[150px] sm:w-[185px] md:w-[200px] snap-start bg-white rounded-xl overflow-hidden text-slate-800 hover:-translate-y-0.5 transition-all shadow-sm">
      <div className="aspect-square bg-slate-100 relative overflow-hidden">
        {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-9 h-9" /></div>}
        {disc > 0 && <span className="absolute bottom-2 left-2 bg-[#DC2626] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">-{disc}%</span>}
      </div>
      <div className="px-2.5 pt-2">
        <div className="h-1.5 rounded-full bg-amber-100 overflow-hidden">
          <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[10px] text-slate-500 mt-1">{shown > 0 ? `${shown} ${limit > 0 ? "restants" : (p.unit || "en stock")}` : "Bientôt épuisé"}</p>
      </div>
      <div className="px-2.5 pb-3 pt-1">
        <h3 className="text-xs sm:text-sm font-semibold line-clamp-2 leading-snug min-h-[2.25rem]">{p.name}</h3>
        {rating && rating.count > 0 && (
          <div className="mt-1 flex items-center gap-1">
            <Star className="w-3 h-3 fill-[#E08B00] text-[#E08B00]" />
            <span className="text-[11px] font-semibold font-num">{rating.average}</span>
            <span className="text-[10px] text-slate-400">({rating.count})</span>
          </div>
        )}
        <div className="mt-1 flex items-baseline gap-1.5 flex-wrap">
          <span className="font-num font-bold text-[#C85A32] text-sm">{formatFCFA(eff)}</span>
          {disc > 0 && <span className="font-num text-[10px] text-slate-400 line-through">{formatFCFA(oldP)}</span>}
        </div>
      </div>
    </Link>
  );
}
